import { useState } from 'react';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import type { Availability } from '../types';

interface AvailabilityCalendarProps {
  availability: Availability[];
  onDateSelect?: (dates: { start: string; end: string } | null) => void;
  selectedDates?: { start: string; end: string } | null;
}

export function AvailabilityCalendar({ 
  availability, 
  onDateSelect,
  selectedDates 
}: AvailabilityCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectionStart, setSelectionStart] = useState<string | null>(selectedDates?.start || null);
  const [selectionEnd, setSelectionEnd] = useState<string | null>(selectedDates?.end || null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  const availabilityMap = new Map(availability.map(a => [a.date, a]));

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateInRange = (dateStr: string) => {
    if (!selectionStart) return false;
    if (!selectionEnd && !hoverDate) return dateStr === selectionStart;
    
    const start = selectionStart;
    const end = selectionEnd || hoverDate;
    if (!end) return false;
    
    return dateStr >= start && dateStr <= end;
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatDateKey(date);
    
    if (!selectionStart || (selectionStart && selectionEnd)) {
      setSelectionStart(dateStr);
      setSelectionEnd(null);
      onDateSelect?.(null);
    } else {
      if (dateStr < selectionStart) {
        setSelectionStart(dateStr);
        setSelectionEnd(selectionStart);
        onDateSelect?.({ start: dateStr, end: selectionStart });
      } else {
        setSelectionEnd(dateStr);
        onDateSelect?.({ start: selectionStart, end: dateStr });
      }
    }
  };

  const handleMouseEnter = (date: Date) => {
    if (selectionStart && !selectionEnd) {
      setHoverDate(formatDateKey(date));
    }
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="font-semibold text-lg">{monthName}</h3>
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const dateStr = formatDateKey(date);
          const avail = availabilityMap.get(dateStr);
          const isAvailable = !!avail;
          const isSelected = isDateInRange(dateStr);
          const isStart = dateStr === selectionStart;
          const isEnd = dateStr === selectionEnd || (dateStr === hoverDate && !selectionEnd);

          return (
            <button
              key={dateStr}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => handleMouseEnter(date)}
              onMouseLeave={() => setHoverDate(null)}
              disabled={!isAvailable}
              className={`
                aspect-square rounded-lg flex flex-col items-center justify-center text-sm relative
                transition-all duration-200
                ${!isAvailable ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-emerald-50 cursor-pointer'}
                ${isSelected ? 'bg-emerald-100 text-emerald-800' : ''}
                ${isStart ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}
                ${isEnd ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}
              `}
            >
              <span className="font-medium">{date.getDate()}</span>
              {isAvailable && !isSelected && (
                <Check className="w-3 h-3 text-emerald-500 absolute bottom-1" />
              )}
              {!isAvailable && (
                <X className="w-3 h-3 text-gray-400 absolute bottom-1" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-emerald-600 rounded" />
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-emerald-100 rounded border border-emerald-300" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 rounded" />
          <span>Unavailable</span>
        </div>
      </div>
    </div>
  );
}
